<template>
  <ontos-base v-bind="baseProps" ref="base">
    <div v-if="loaded">
      <!-- Sem cabeçalho: o nome é o da aba, Acessos, e repeti-lo logo embaixo seria ruído -->
      <ontos-section :header="false" :divider="false">
        <!-- Acrescentar é uma LINHA no alto, e não um modal: é a ação mais frequente desta
             tela, e um modal para ela seria um clique a mais em tudo.

             O e-mail não precisa ter conta no lunedi: o servidor cria uma, pendente, e o nível
             passa a valer quando a pessoa entrar. É o que dispensa um convite — não há nada
             para aceitar, só um nome na lista.

             Por isso é o seletor de pessoas no modo livre (`free`): ele SUGERE as pessoas do
             lunedi, achadas pelo nome ou pelo e-mail, mas aceita qualquer texto, e o valor é o
             e-mail — escolher alguém da lista é o mesmo que digitar o endereço dele. -->
        <v-form v-if="can('admin')" @submit.prevent="addMember()">
          <v-row align="start" class="mb-2">
            <!-- `:error-messages` acertado quando o valor é confirmado e no envio, como o login e
                 o cadastro: com `:rules` o Vuetify confere a cada tecla, e o e-mail nasce inválido
                 enquanto se digita. Apagar o campo volta a ser "obrigatório", e não "inválido".

                 Com o escolhido desenhado como pessoa (o slot `selection` do seletor), o combobox
                 só passa o texto ao valor quando se CONFIRMA (sair do campo, Enter, escolher), e
                 não a cada tecla; por isso a conferência do e-mail está no `update:model-value`, e
                 não no blur, e o erro sai quando se volta a digitar.

                 Campo de texto, e não `type="email"`: o navegador apara os espaços desse tipo
                 enquanto se digita, e "Joana Silva" não se escrevia na busca. -->
            <v-col cols="12" sm="6" md="5">
              <ontos-person-select free v-model="email" :people="invitable"
                                   :label="$tt('member-search')"
                                   :error-messages="emailError" :hint="unknownEmailHint" persistent-hint
                                   @update:model-value="validateEmail($event)"
                                   @update:search="onSearch($event)"
                                   density="compact" variant="outlined" hide-details="auto"/>
            </v-col>
            <v-col cols="8" sm="4" md="3">
              <ontos-colored-select v-model="level" :items="levels" :label="$tt('access-level')"/>
            </v-col>
            <!-- Sempre habilitado: quem valida é o envio, e botão que acende e apaga enquanto
                 se digita é a mesma pressa do `:rules`. -->
            <v-col cols="4" sm="2" class="pt-4">
              <v-btn type="submit" variant="text" color="primary">
                <v-icon start>mdi-account-plus</v-icon>
                {{ $tt('member-add') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <!-- A frase explica a linha SEM AÇÕES logo abaixo, e só aparece quando há uma: botão
             ausente não explica a própria ausência, e quem é o único administrador veria o seu
             nível travado sem saber por quê.

             Na primeira pessoa porque é sempre com você: só administrador mexe nesta lista, e
             se há um administrador só, esse um é quem está lendo. -->
        <div v-if="onlyAdmin" class="text-body-2 text-grey mb-2">{{ $tt('last-admin-note') }}</div>

        <!-- Os nomes das colunas (pedido do usuário), nas medidas da linha de pessoa: o avatar e o
             nome dela, e depois as ações — o nível numa célula de largura fixa, e os papéis -->
        <div class="members-head text-caption text-medium-emphasis">
          <span class="head-user">{{ $tt('user') }}</span>
          <span class="level-cell">{{ $tt('access-level') }}</span>
          <span v-if="roleItems.length">{{ $tt('roles') }}</span>
        </div>
        <v-list density="compact">
          <v-list-item v-for="member of members" :key="member.user">
            <ontos-person-line :user="member.user" :email="member.email" :name="member.name" :photo="member.photo">
              <!-- Travado, o nível aparece como o chip que ele é; mexível, como o seletor.
                   É a mesma forma dos dois lados — ver o `ValueForm`: nível é opção de um
                   domínio fechado de quatro. -->
              <div class="level-cell">
                <ontos-colored-value v-if="!can('admin') || locked(member)"
                                     :option="levelOption(member.accessLevel)" form="chip"/>
                <ontos-colored-select v-else :model-value="member.accessLevel" :items="levels"
                                      variant="plain" @update:model-value="changeLevel(member, $event)"/>
              </div>
              <!-- Os papéis, só no ambiente que tem algum: vários por pessoa, no mesmo seletor e nos
                   mesmos chips coloridos do nível (pedido do usuário). O último administrador também
                   os muda — o que é travado nele é o nível, e não o que ele faz. -->
              <template v-if="roleItems.length">
                <ontos-colored-select v-if="can('admin')" multiple :model-value="member.roles || []"
                                      :items="roleItems" :placeholder="$tt('roles')" variant="plain"
                                      class="roles-select" @update:model-value="changeRoles(member, $event)"/>
                <span v-else class="d-inline-flex flex-wrap ga-1">
                  <ontos-colored-value v-for="role of rolesOf(member)" :key="role.value" :option="role" form="chip"/>
                </span>
              </template>
              <v-btn v-if="can('admin') && !locked(member)" variant="text" icon size="small"
                     color="error" :title="$tt('remove')" @click="removing = member">
                <v-icon>mdi-account-remove</v-icon>
              </v-btn>
            </ontos-person-line>
          </v-list-item>
        </v-list>
      </ontos-section>

      <!-- Tirar alguém passa pela mesma pergunta das três do ambiente, no mesmo chassi. -->
      <ontos-ask-dialog v-if="removing"
                        :title="$tt('member-remove-confirm', {email: removing.email})"
                        :message="$tt('member-remove-explain')"
                        :actions="removeActions"
                        @pick="onRemovePicked($event)"
                        @closed="removing = null"/>
    </div>
  </ontos-base>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import WorkspaceRouteComponent from '@/components/shared/WorkspaceRouteComponent'
import Section from '@/components/shared/Section.vue'
import AskDialog, {Ask} from '@/components/shared/AskDialog.vue'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import ColoredSelect from '@/components/shared/ColoredSelect.vue'
import PersonLine from '@/components/shared/PersonLine.vue'
import PersonSelect from '@/components/shared/PersonSelect.vue'
import {AccessLevel, levelName, levelOption, levelOptions, Member} from '@/types/AccessLevel'
import {Person} from '@/types/Person'
import {roleOptions} from '@/types/Workspace'
import {saveErrorMessage} from '@/utils/saveErrors'
import {NotUniqueError, NotValidError} from '@/errors/errors'

/**
 * Quem participa deste ambiente, com que nível de acesso e com que papéis: a aba Acessos do design
 * do ambiente, só do administrador. Era uma tela, o terceiro modo da casca; virou aba (pedido do
 * usuário). Continua sendo um `WorkspaceRouteComponent` — carrega ao montar, e avisa por
 * `showMessage`/`showErrorMessage` —, só que hospedado pela aba, que repassa os avisos.
 *
 * Sem rotas de criar e editar à parte: a lista é curta e se edita no lugar — o nível e os papéis
 * trocam nos seletores da própria linha, e tirar alguém é uma pergunta. Rotas separadas existem
 * onde há formulário com rascunho a perder, e aqui não há.
 *
 * **A lista vem do servidor, e não do `workspace`**: ela não cabe no payload que todo membro
 * recebe, justamente porque saber quem mais participa é assunto de administrador. O nível de
 * QUEM OLHA, esse sim, chega no ambiente — é com ele que o resto do app decide o que desenhar.
 */
@Component({
  components: {
    'ontos-section': Section,
    'ontos-ask-dialog': AskDialog,
    'ontos-colored-value': ColoredValue,
    'ontos-colored-select': ColoredSelect,
    'ontos-person-line': PersonLine,
    'ontos-person-select': PersonSelect
  }
})
class MembersPanel extends WorkspaceRouteComponent {
  members: Member[] = []
  /** As contas admitidas do lunedi, fora você — ver o `invitable`. */
  known: Person[] = []
  //O combobox devolve null quando é limpo, e não ''
  email: string | null = ''
  emailError = ''
  level: AccessLevel = 'editor'
  removing: Member | null = null

  //expostos ao template: função de módulo não é enxergada de lá
  levelOption = levelOption
  levels = levelOptions()

  /** Os papéis do ambiente, que o design cria, como opções coloridas — ver `roleOptions`. */
  get roleItems() {
    return roleOptions(this.workspace.roles || [])
  }

  /** Os papéis de uma pessoa, na ordem em que o ambiente os lista. */
  rolesOf(member: Member) {
    return this.roleItems.filter(role => (member.roles || []).includes(role.value))
  }

  async load() {
    try {
      this.members = await this.$members.list({systemAlias: this.workspace.alias})
    } catch (err) {
      this.throwError(this.$tt('error.load-members'), err)
    }
    //A sugestão é comodidade: se ela falha, o campo continua aceitando o e-mail digitado, e a tela
    //não merece um aviso vermelho por isso. Mas fica no console: engolida em silêncio, a falha
    //(um servidor local sem a rota) pareceu "a pessoa não aparece" e custou um diagnóstico
    if (this.can('admin')) this.known = await this.$members.known().catch(err => {
      console.warn('people suggestions unavailable', err)
      return []
    })
  }

  /**
   * Quem a lista sugere: as pessoas do lunedi, menos as que já estão neste ambiente. O servidor
   * não tira estas porque a lista delas está aqui, e muda na tela: tirar alguém o devolve à
   * sugestão sem nova consulta.
   */
  get invitable(): Person[] {
    const here = new Set(this.members.map(member => member.user))
    return this.known.filter(person => !here.has(person.user))
  }

  /**
   * O aviso de que o e-mail não é de ninguém conhecido — e o convite segue mesmo assim.
   *
   * Lê o valor CONFIRMADO, e não o que se digita: enquanto se busca um nome não há aviso. Um nome
   * confirmado como valor não é e-mail, e aí quem fala é o erro, que o Vuetify mostra no lugar
   * deste aviso. Conhecido é quem está na sugestão ou já neste ambiente (o de lá recusa o
   * servidor, como duplicado). Uma conta pendente cai aqui como desconhecida, porque não é
   * sugerida (decisão do usuário).
   */
  get unknownEmailHint(): string {
    const typed = (this.email || '').trim().toLowerCase()
    if (!typed) return ''
    const known = [...this.known, ...this.members].some(person => person.email === typed)
    return known ? '' : this.$tt('member-unknown-email') as string
  }

  /**
   * O id do único administrador, ou null havendo mais de um.
   *
   * Ambiente sem administrador não tem quem o conserte — ninguém poderia nem devolver o nível
   * a alguém —, então o último não sai nem se rebaixa. **Quem esbarra nisso é sempre a própria
   * pessoa**: só administrador mexe nesta lista, e havendo um só, é ele quem está aqui. Quem
   * garante é o servidor; isto aqui é o que a tela desenha.
   */
  get onlyAdmin(): number | null {
    const admins = this.members.filter(member => member.accessLevel === 'admin')
    return admins.length === 1 ? admins[0].user : null
  }

  locked(member: Member): boolean {
    return member.user === this.onlyAdmin
  }

  /**
   * Confirmado o valor, uma pergunta só: **o que está aí é um e-mail?**
   *
   * Vazio não é erro aqui. Dizer "obrigatório" a quem passou pelo campo é repreender por algo
   * que a pessoa ainda não fez — talvez ela nem fosse acrescentar ninguém. Essa pergunta é do
   * botão, e só dele.
   *
   * **Quem diz se é um e-mail é o navegador**, pela regra do HTML para `type="email"` — a mesma
   * com que ele barraria o envio de um formulário nativo. Lida pelo `validity.typeMismatch` de um
   * campo avulso, porque o da tela é de texto (ver o template), ela evita a expressão regular de
   * sempre, que acerta o óbvio e recusa o válido raro.
   */
  validateEmail(value: string | null) {
    const probe = document.createElement('input')
    probe.type = 'email'
    probe.value = value || ''
    this.emailError = probe.validity.typeMismatch ? this.$tt('auth.invalid-email') as string : ''
  }

  /**
   * Voltar a digitar tira o erro. Só com texto: ao confirmar, o combobox esvazia a busca DEPOIS de
   * passar o valor, e esse esvaziamento apagaria o erro que a conferência acabou de dar.
   */
  onSearch(text: string | null) {
    if (text) this.emailError = ''
  }

  get removeActions(): Ask[] {
    return [
      {value: 'remove', label: this.$tt('remove') as string, icon: 'mdi-account-remove', color: 'error'},
      {value: 'cancel', label: this.$tt('cancel') as string, icon: 'mdi-cancel', text: true}
    ]
  }

  async addMember() {
    const email = this.email || ''
    this.emailError = this.required(email)
    if (this.emailError) return
    try {
      await this.saving(async () => {
        //Guarda o que o servidor DEVOLVEU: é ele quem apara o e-mail e baixa a caixa, e a lista
        //tem de ficar com a forma que vai voltar na próxima leitura
        const added = await this.$members.add({systemAlias: this.workspace.alias}, {email, accessLevel: this.level})
        this.members.push(added)
        this.info(this.$tt('info.member-added', {email: added.email, level: levelName(added.accessLevel)}))
        this.email = ''
      })
    } catch (err: any) {
      //As duas recusas que são DO CAMPO vão para o campo, e não para o aviso vermelho lá
      //embaixo: erro de campo em snackbar faz o usuário procurar onde foi. É o que o cadastro
      //já faz com o e-mail em uso e com o formato recusado.
      if (err instanceof NotUniqueError)
        this.emailError = this.$tt('validation.already-used', {value: email}) as string
      else if (err instanceof NotValidError && err.data && err.data.property === 'email')
        this.emailError = this.$tt('auth.invalid-email') as string
      else this.throwError(saveErrorMessage(err) || this.$tt('error.add-member', {email}), err)
    }
  }

  async changeLevel(member: Member, accessLevel: AccessLevel) {
    await this.attempt(this.$tt('error.change-access-level', {email: member.email}), async () => {
      await this.$members.update({systemAlias: this.workspace.alias, user: member.user}, {accessLevel})
      member.accessLevel = accessLevel
      this.info(this.$tt('info.member-level-changed', {email: member.email, level: levelName(accessLevel)}))
    })
  }

  /** A cada papel marcado ou tirado — a lista é curta, e o seletor é da própria linha. */
  async changeRoles(member: Member, roles: string[]) {
    await this.attempt(this.$tt('error.change-roles', {email: member.email}), async () => {
      await this.$members.update({systemAlias: this.workspace.alias, user: member.user}, {roles})
      member.roles = roles
    })
  }

  async onRemovePicked(action: string | null) {
    const member = this.removing
    this.removing = null
    if (action !== 'remove' || !member) return
    await this.attempt(this.$tt('error.remove-member', {email: member.email}), async () => {
      await this.$members.remove({systemAlias: this.workspace.alias, user: member.user})
      this.members = this.members.filter(current => current.user !== member.user)
      this.info(this.$tt('info.member-removed', {email: member.email}))
    })
  }

  /**
   * As escritas que não têm campo onde reclamar — trocar o nível ou os papéis, e tirar alguém.
   *
   * A recusa legível sai do `saveErrorMessage`, que é o dono de "traduzir o `validation` do
   * servidor" e já servia ao formulário de item e ao Kanban — as recusas de nível e de papel entraram
   * lá, e não aqui, para não haver duas listas de recusas. É por ele que chega o "um ambiente
   * precisa de ao menos um administrador".
   */
  private async attempt(fallback: string, write: () => Promise<void>) {
    try {
      await this.saving(write)
    } catch (err: any) {
      this.throwError(saveErrorMessage(err) || fallback, err)
    }
  }
}

export default toNative(MembersPanel)
</script>

<style scoped>
/* O seletor acompanha o nome na mesma linha em vez de esticar: a lista é de e-mails, que são
   longos, e um select de largura livre empurraria o nome para as reticências. */
/* O nível numa largura fixa, chip travado ou seletor: é o que alinha a coluna dos papéis de uma
   linha para a outra, e com o cabeçalho. */
.level-cell {
  flex: 0 0 180px;
}

/* Os papéis ao lado do nível: cabem alguns chips, e o resto quebra dentro do próprio seletor */
.roles-select {
  min-width: 160px;
  max-width: 360px;
}

/* O cabeçalho nas medidas da linha de pessoa (ver o PersonLine): o recuo do v-list-item, o avatar
   de 36px e o nome de até 280px, com o vão de 12px entre eles; depois o vão até as ações (12px da
   linha e 16px da margem delas), e as ações com 8px entre si. */
.members-head {
  display: flex;
  gap: 8px;
  padding: 0 16px;
}

.head-user {
  flex: 0 1 328px;
  margin-right: 20px;
  min-width: 0;
}
</style>
